import { createSignal, Match, onMount, Switch } from "solid-js";
import Cookies from "js-cookie";
import { z } from "zod";

import Promotion from "@repo/kikoff-models/Promotion";
import Trans from "@repo/localization/jsx.macro";
import t from "@repo/localization/string.macro";
import Expand from "@repo/ui/animation/Expand";
import Fade from "@repo/ui/animation/Fade";
import Logo from "@repo/ui/branding/Logo";
import LoadableButton from "@repo/ui/buttons/LoadableButton";
import Card from "@repo/ui/containers/Card";
import Checkbox from "@repo/ui/inputs/Checkbox";
import Form from "@repo/ui/inputs/Form";
import TextInput from "@repo/ui/inputs/TextInput";
import Link from "@repo/ui/navigation/Link";
import createForm from "@repo/ui/signals/createForm";
import Money from "@repo/utils/Money";

import analytics, { track } from "~/analytics/client";
import AuthButtons from "~/components/auth/AuthButtons";
import AuthLayout from "~/components/auth/AuthLayout";
import PasswordInput from "~/components/auth/PasswordInput";
import PasswordStrength from "~/components/auth/PasswordStrength";
import SignupOffer from "~/components/auth/SignupOffer";
import { usePromotions } from "~/state/features/promotions";
import { useUser } from "~/state/features/user";
import UserLead from "~/utils/UserLead";

export default function Signup() {
  const [user] = useUser();
  const [promotions, setPromotions] = usePromotions();

  onMount(() => {
    if (
      !promotions.signup() &&
      (Cookies.get("signup-promo") || UserLead.introPromoEligible())
    ) {
      setPromotions.fetchSignupPromo().then((promo) => {
        if (promo) Cookies.set("signup-promo", promo.id);
        else Cookies.remove("signup-promo");
      });
    }
  });

  return (
    <AuthLayout.Signup>
      <Logo class="text:heading-3" word icon />
      <Switch>
        <Match when={user.referral()?.amountCents! > 0 && user.referral()}>
          {(referral) => (
            <SignupOffer class="pt-4">
              <Trans>
                {referral().referrer.firstName} sent you a{" "}
                {Money.format(referral().amountCents)} gift. Sign up to redeem.
              </Trans>
            </SignupOffer>
          )}
        </Match>
        <Match when={promotions.signup()}>
          {(promo) => (
            <SignupOffer class="pt-4">
              {Promotion.description(promo())}
            </SignupOffer>
          )}
        </Match>
        <Match when={true}>
          <AuthLayout.MobileOnly>
            <Card class="bg:accent color:on-accent py-2.5 mt-3">
              <header class="text:large++ mb-1">
                <span class="icon:medium"></span> <Trans>+84 points*</Trans>
              </header>
              <Trans>
                Users with starting credit under 600 grew an average of{" "}
                <b>+84pts</b> in just a year with on-time payments.
              </Trans>
            </Card>
          </AuthLayout.MobileOnly>
        </Match>
      </Switch>

      <header class="text:heading-3++ mt-3 mb-2">
        <Trans>Create your account</Trans>
      </header>
      <SignupForm />
      <Link href="/login" variant="container" class="mt-3">
        <Trans>
          Already have an account?{" "}
          <span class="underline text:regular+">Log in here</span>
        </Trans>
      </Link>

      <div class="text:mini color:moderate mt-8">
        <Trans>
          * Average first-month credit score impact of +25 points (VantageScore
          3.0) between Jan-2024 & Nov-2024 for Kikoff Credit Account users who
          started with a score below 600; who purchased at least one item with
          Credit Account; and who paid one on-time in their first month. Average
          first-year credit score impact of +84 points (VantageScore 3.0)
          between Jan-2023 & Jan-2024 for Kikoff Credit Account users who
          started with a score below 600; who paid on-time; and who had no
          delinquencies or collections added to their credit profile during the
          period. Late payments may negatively impact your credit score.
          Individual results may vary.
        </Trans>
      </div>
    </AuthLayout.Signup>
  );
}

function SignupForm() {
  const [, setUser] = useUser();

  const [successText, setSuccessText] = createSignal(t`Success`);
  const [acknowledged, setAcknowledged] = createSignal(false);
  const [showAgreementError, setShowAgreementError] = createSignal(false);

  const { register, handleSubmit, submittable } = createForm({
    schema: z.object({
      email: z.string({ required_error: t`Required` }).email(t`Invalid email`),
      password: z.string({ required_error: t`Required` }).min(8, t`Too short`),
      passwordScore: z
        .number()
        .min(1, t`Too weak`)
        .default(0),
    }),
  });

  return (
    <Form
      onSubmit={(e) => {
        track.onboarding.createAccount.submitButton("click");

        return handleSubmit((_, data) => {
          if (!acknowledged()) {
            setShowAgreementError(true);
            throw null;
          }

          return setUser.create
            .defer(data)
            .then(({ authenticated, resolveUser }) => {
              if (!authenticated) analytics.convert("Signup");

              setSuccessText(authenticated ? t`Logged in` : t`Created`);
              LoadableButton.afterSuccess(() => {
                resolveUser();
              });
            });
        })(e);
      }}
    >
      <TextInput
        class="mb-1"
        label={t`Email`}
        type="email"
        autocomplete="email"
        {...register("email")}
      />
      {(() => {
        const [passwordFocusedOnce, setPasswordFocusedOnce] =
          createSignal(false);

        return (
          <>
            <PasswordInput
              class="mb-1"
              label={t`Password`}
              autocomplete="new-password"
              onFocus={() => {
                setPasswordFocusedOnce(true);
              }}
              {...register("password")}
              error={
                register("password").error || register("passwordScore").error
              }
            />
            <Expand direction="y" show={passwordFocusedOnce()}>
              <Fade
                direction="up"
                transition="var(--normal-s) calc(var(--normal-s) / 2)"
              >
                <PasswordStrength
                  password={register("password").value!}
                  onScoreChange={(score) => {
                    register("passwordScore").value = score;
                  }}
                />
              </Fade>
            </Expand>
          </>
        );
      })()}

      <Checkbox.WithLabel
        class="my-2 text:small"
        checked={acknowledged()}
        onClick={() => {
          setAcknowledged(!acknowledged());
          setShowAgreementError(false);
        }}
        error={
          showAgreementError() &&
          t`Please accept the Terms of Service, E-Sign Consent, and Privacy Policy to continue.`
        }
      >
        <Trans>
          Accept Kikoff’s{" "}
          <Link.Doc href={t`/terms.pdf`}>Terms of Service</Link.Doc>,{" "}
          <Link.Doc href={t`/e-sign.pdf`}>E-Sign Consent</Link.Doc>, and{" "}
          <Link.Doc href={t`/privacy-policy.pdf`}>Privacy Policy</Link.Doc>.
        </Trans>
      </Checkbox.WithLabel>

      <AuthButtons
        submittable={submittable()}
        acknowledged={acknowledged()}
        text={{
          initial: t`Sign up`,
          pending: "",
          success: successText(),
        }}
        onClick={() => {
          if (!acknowledged()) setShowAgreementError(true);
        }}
      />
    </Form>
  );
}
